import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertCircle,
  BadgePercent,
  Bell,
  Camera,
  CheckCircle2,
  ClipboardCheck,
  Coins,
  Loader2,
  MessageCircleMore,
  QrCode,
  RefreshCw,
  Save,
  Smartphone,
  Sparkles,
  Store,
} from 'lucide-react';

import ClientAppSimulator from '../../components/ClientAppSimulator';
import ClientBannerManager from '../../components/ClientBannerManager';
import ClientCampaignManager from '../../components/ClientCampaignManager';
import ClientCatalogManager from '../../components/ClientCatalogManager';
import ClientChatManager from '../../components/ClientChatManager';
import ClientOrderModerationPanel from '../../components/ClientOrderModerationPanel';
import ModalShell from '../../components/ModalShell';
import { API_URL } from '../../config';
import useToast from '../../hooks/useToast';
import { haptic } from '../../utils/vibration';
import * as servicesApi from '../../services/servicesApi';


const clientAppRequest = async (path, options = {}) => {
  const response = await fetch(`${API_URL}/api/client-catalog-admin/v1${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${localStorage.getItem('token')}`,
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      ...(options.headers || {}),
    },
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const detail = payload?.detail;
    throw new Error(typeof detail === 'string' ? detail : detail?.message || 'Не удалось выполнить действие');
  }
  return payload;
};


const CLIENT_APP_SECTIONS = [
  { id: 'simulator', label: 'Эмулятор', caption: 'Интерактивный тест', icon: Smartphone },
  { id: 'requests', label: 'Заявки', caption: 'Проверка и подтверждение', icon: ClipboardCheck },
  { id: 'chats', label: 'Чаты', caption: 'Диалоги с клиентами', icon: MessageCircleMore },
  { id: 'banners', label: 'Баннеры', caption: 'Промо-акции на главной', icon: Sparkles },
  { id: 'campaigns', label: 'Рассылки', caption: 'Пуши и сегменты клиентов', icon: Bell },
  { id: 'catalog', label: 'Витрина', caption: 'Услуги и подразделения', icon: Store },
  { id: 'loyalty', label: 'Лояльность', caption: 'QR-скидка и бонусы', icon: BadgePercent },
];


const ClientQrScanner = ({ open, onClose, onDetected }) => {
  const videoRef = useRef(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    let stream = null;
    let timer = null;

    const start = async () => {
      setError('');
      if (!navigator.mediaDevices?.getUserMedia || !window.BarcodeDetector) {
        setError('Автосканирование недоступно на этом устройстве. Введите резервный код вручную.');
        return;
      }
      try {
        const detector = new window.BarcodeDetector({ formats: ['qr_code'] });
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } },
          audio: false,
        });
        if (!active || !videoRef.current) return;
        videoRef.current.srcObject = stream;
        await videoRef.current.play();

        const scan = async () => {
          if (!active || !videoRef.current) return;
          try {
            const codes = await detector.detect(videoRef.current);
            const value = codes?.[0]?.rawValue;
            if (value) {
              active = false;
              onDetected(value);
              return;
            }
          } catch (_) {
            // Камера ещё не успела подготовить следующий кадр.
          }
          timer = window.setTimeout(scan, 350);
        };
        timer = window.setTimeout(scan, 350);
      } catch (_) {
        setError('Не удалось открыть камеру. Разрешите доступ или введите резервный код.');
      }
    };

    start();
    return () => {
      active = false;
      if (timer) window.clearTimeout(timer);
      if (stream) stream.getTracks().forEach((track) => track.stop());
    };
  }, [open, onDetected]);

  return (
    <ModalShell open={open} onClose={onClose} variant="window" panelClassName="max-w-md overflow-hidden p-0">
      <div className="border-b border-gray-100 px-4 py-3 dark:border-gray-800">
        <div className="flex items-center gap-2 font-semibold text-gray-900 dark:text-white"><QrCode className="h-5 w-5 text-blue-600" />Сканировать QR клиента</div>
        <p className="mt-1 text-xs text-gray-500">Код обновляется автоматически и действует недолго.</p>
      </div>
      <div className="p-4">
        <div className="aspect-square overflow-hidden rounded-2xl bg-black">
          <video ref={videoRef} muted playsInline className="h-full w-full object-cover" />
        </div>
        {error && <p className="mt-3 rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-700 dark:bg-amber-950/30 dark:text-amber-200">{error}</p>}
        <button type="button" onClick={onClose} className="mt-4 w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-700 dark:border-gray-700 dark:text-gray-200">Закрыть</button>
      </div>
    </ModalShell>
  );
};


const ClientAppManagement = () => {
  const [activeSection, setActiveSection] = useState('requests');
  const [services, setServices] = useState([]);
  const [settings, setSettings] = useState({
    loyalty_mode: 'qr_discount',
    qr_discount_percent: 5,
    qr_ttl_seconds: 60,
  });
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { showToast } = useToast();
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [credential, setCredential] = useState('');
  const [validationResult, setValidationResult] = useState(null);
  const [validating, setValidating] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [servicesPayload, settingsPayload, statusPayload] = await Promise.all([
        servicesApi.getServices({ active_only: true, client_type: 'b2c' }),
        clientAppRequest('/settings'),
        clientAppRequest('/status').catch(() => null),
      ]);
      setServices(Array.isArray(servicesPayload) ? servicesPayload : []);
      setSettings(settingsPayload);
      if (statusPayload) {
        setStatus(statusPayload);
      }
    } catch (loadError) {
      const msg = loadError.message || 'Не удалось загрузить управление клиентским приложением';
      setError(msg);
      showToast(msg, 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const saveSettings = async () => {
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const payload = await clientAppRequest('/settings', {
        method: 'PUT',
        body: JSON.stringify({
          loyalty_mode: settings.loyalty_mode,
          qr_discount_percent: Number(settings.qr_discount_percent),
          qr_ttl_seconds: Number(settings.qr_ttl_seconds || 60),
        }),
      });
      setSettings(payload);
      setValidationResult(null);
      setNotice('Настройки клиентской программы сохранены');
      showToast('Настройки клиентской программы сохранены', 'success');
    } catch (saveError) {
      setError(saveError.message);
      showToast(saveError.message || 'Не удалось сохранить настройки', 'error');
    } finally {
      setSaving(false);
    }
  };

  const validateCredential = useCallback(async (rawValue = credential) => {
    const normalized = String(rawValue || '').trim();
    if (!normalized) return;
    setValidating(true);
    setError('');
    setNotice('');
    setValidationResult(null);
    try {
      const payload = await clientAppRequest('/loyalty/validate', {
        method: 'POST',
        body: JSON.stringify({ credential: normalized }),
      });
      setCredential(normalized);
      setValidationResult(payload);
      setScannerOpen(false);
      showToast('Карта подтверждена', 'success');
    } catch (validationError) {
      setError(validationError.message);
      showToast(validationError.message || 'Ошибка проверки карты', 'error');
      setScannerOpen(false);
    } finally {
      setValidating(false);
    }
  }, [credential]);

  return (
    <div className="space-y-3 animate-fade-in desktop-content-container-wide">
      <section className="overflow-hidden rounded-2xl border border-blue-100 bg-gradient-to-br from-slate-900 via-blue-950 to-blue-700 p-4 text-white shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/15">
              <MessageCircleMore className="h-6 w-6" />
            </span>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="font-bold text-base sm:text-lg">Управление клиентским приложением</h1>
                {status?.client_backend_alive ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/20 px-2 py-0.5 text-xs font-semibold text-emerald-200 border border-emerald-400/30">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    Бэкенд :5100 онлайн
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded-full bg-slate-500/30 px-2 py-0.5 text-xs text-slate-300 border border-slate-400/20">
                    Бэкенд :5100 офлайн
                  </span>
                )}
              </div>
              <p className="mt-1 max-w-2xl text-xs text-blue-100/90">
                Закрытый раздел разработчика: эмулятор клиента, модерация заявок, чаты, витрина услуг и лояльность.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {Number(status?.pending_requests_count) > 0 && (
              <button
                type="button"
                onClick={() => setActiveSection('requests')}
                className="inline-flex items-center gap-1.5 rounded-xl bg-amber-500 px-3 py-1.5 text-xs font-bold text-white shadow hover:bg-amber-600 transition"
              >
                <span>{status.pending_requests_count}</span>
                <span className="font-normal opacity-90">новых заявок</span>
              </button>
            )}
            {Number(status?.unread_messages_count) > 0 && (
              <button
                type="button"
                onClick={() => setActiveSection('chats')}
                className="inline-flex items-center gap-1.5 rounded-xl bg-blue-500 px-3 py-1.5 text-xs font-bold text-white shadow hover:bg-blue-600 transition"
              >
                <span>{status.unread_messages_count}</span>
                <span className="font-normal opacity-90">новых сообщений</span>
              </button>
            )}
          </div>
        </div>
      </section>

      <nav className="grid grid-cols-2 gap-2 rounded-2xl bg-white p-2 shadow-sm dark:bg-gray-900 sm:grid-cols-4 lg:grid-cols-7" aria-label="Разделы клиентского приложения">
        {CLIENT_APP_SECTIONS.map((section) => {
          const Icon = section.icon;
          const active = activeSection === section.id;
          const count = section.id === 'requests'
            ? status?.pending_requests_count
            : section.id === 'chats'
              ? status?.unread_messages_count
              : null;

          return (
            <button
              type="button"
              key={section.id}
              onClick={() => { haptic('selection'); setActiveSection(section.id); }}
              className={`flex items-center gap-2 rounded-xl border p-3 text-left transition ${
                active
                  ? 'border-blue-400 bg-blue-50 text-blue-800 dark:border-blue-700 dark:bg-blue-950/30 dark:text-blue-200'
                  : 'border-transparent text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800'
              }`}
            >
              <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${active ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-300'}`}>
                <Icon className="h-[18px] w-[18px]" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center justify-between">
                  <strong className="block text-sm">{section.label}</strong>
                  {Boolean(count && count > 0) && (
                    <span className={`ml-1.5 rounded-full px-1.5 py-0.2 text-[10px] font-bold text-white ${section.id === 'requests' ? 'bg-amber-500' : 'bg-blue-600'}`}>
                      {count}
                    </span>
                  )}
                </span>
                <small className="hidden truncate text-[11px] opacity-70 sm:block">{section.caption}</small>
              </span>
            </button>
          );
        })}
      </nav>

      {activeSection === 'simulator' && (
        <ClientAppSimulator
          status={status}
          onRefreshStatus={loadData}
          onNavigateSection={setActiveSection}
        />
      )}
      {activeSection === 'requests' && <ClientOrderModerationPanel />}
      {activeSection === 'chats' && <ClientChatManager />}
      {activeSection === 'banners' && <ClientBannerManager services={services} />}
      {activeSection === 'campaigns' && <ClientCampaignManager />}

      {activeSection === 'catalog' && (
        loading ? (
          <div className="flex items-center justify-center rounded-2xl bg-white p-12 text-gray-500 shadow-sm dark:bg-gray-900"><Loader2 className="mr-2 h-5 w-5 animate-spin" />Загружаем витрину…</div>
        ) : error && !services.length ? (
          <ConfigurationError error={error} onRetry={loadData} />
        ) : <ClientCatalogManager services={services} />
      )}

      {activeSection === 'loyalty' && (
        loading ? (
          <div className="flex items-center justify-center rounded-2xl bg-white p-12 text-gray-500 shadow-sm dark:bg-gray-900"><Loader2 className="mr-2 h-5 w-5 animate-spin" />Загружаем программу лояльности…</div>
        ) : error && !services.length ? (
          <ConfigurationError error={error} onRetry={loadData} />
        ) : (
          <>
            <section className="rounded-2xl bg-white p-4 shadow-sm dark:bg-gray-900">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div><div className="flex items-center gap-2 text-sm font-bold text-gray-900 dark:text-white"><BadgePercent className="h-5 w-5 text-blue-600" />Программа лояльности</div><p className="mt-1 max-w-2xl text-xs text-gray-500">Выберите, что клиент видит в приложении. На первом этапе включена динамическая QR-скидка; бонусный режим показывает баланс из рабочей клиентской базы.</p></div>
                <button type="button" disabled={saving} onClick={saveSettings} className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}Сохранить</button>
              </div>

              <div className="mt-4 grid gap-3 md:grid-cols-2">
                <button type="button" onClick={() => setSettings((current) => ({ ...current, loyalty_mode: 'qr_discount' }))} className={`rounded-2xl border p-4 text-left transition-colors ${settings.loyalty_mode === 'qr_discount' ? 'border-blue-400 bg-blue-50 dark:border-blue-700 dark:bg-blue-950/30' : 'border-gray-200 dark:border-gray-700'}`}><div className="flex items-center gap-2 font-semibold text-gray-900 dark:text-white"><QrCode className="h-5 w-5 text-blue-600" />QR-скидка</div><p className="mt-1 text-xs text-gray-500">Короткоживущий QR и резервный код для подтверждения скидки постоянного клиента.</p></button>
                <button type="button" onClick={() => setSettings((current) => ({ ...current, loyalty_mode: 'bonuses' }))} className={`rounded-2xl border p-4 text-left transition-colors ${settings.loyalty_mode === 'bonuses' ? 'border-violet-400 bg-violet-50 dark:border-violet-700 dark:bg-violet-950/30' : 'border-gray-200 dark:border-gray-700'}`}><div className="flex items-center gap-2 font-semibold text-gray-900 dark:text-white"><Coins className="h-5 w-5 text-violet-600" />Бонусы</div><p className="mt-1 text-xs text-gray-500">В клиентском приложении отображается текущий бонусный баланс из карточки клиента.</p></button>
              </div>

              {settings.loyalty_mode === 'qr_discount' && <label className="mt-4 block max-w-xs text-xs font-medium text-gray-600 dark:text-gray-300">Размер скидки постоянного клиента<div className="mt-1 flex items-center rounded-xl border border-gray-200 bg-white px-3 dark:border-gray-700 dark:bg-gray-800"><input type="number" min="0.1" max="100" step="0.1" value={settings.qr_discount_percent} onChange={(event) => setSettings((current) => ({ ...current, qr_discount_percent: event.target.value }))} className="min-w-0 flex-1 bg-transparent py-2.5 text-sm outline-none dark:text-white" /><span className="text-sm text-gray-500">%</span></div></label>}
            </section>

            {(error || notice) && <div className={`rounded-xl border px-4 py-3 text-sm ${error ? 'border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200' : 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-200'}`}>{error || notice}</div>}

            {settings.loyalty_mode === 'qr_discount' && (
              <section className="rounded-2xl bg-white p-4 shadow-sm dark:bg-gray-900">
                <div className="flex items-start gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
                    <Camera className="h-5 w-5" />
                  </span>
                  <div>
                    <h3 className="text-sm font-bold text-gray-900 dark:text-white">Тестовый считыватель QR</h3>
                    <p className="mt-1 text-xs text-gray-500">Откройте QR в клиентском приложении и проверьте его камерой либо резервным кодом.</p>
                  </div>
                </div>
                <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                  <input
                    value={credential}
                    onChange={(event) => setCredential(event.target.value)}
                    onKeyDown={(event) => event.key === 'Enter' && validateCredential()}
                    placeholder="Например, P12-12345678"
                    className="min-w-0 flex-1 rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-blue-400 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                  />
                  <button
                    type="button"
                    onClick={() => setScannerOpen(true)}
                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-blue-200 px-4 py-2.5 text-sm font-semibold text-blue-700 dark:border-blue-800 dark:text-blue-300"
                  >
                    <Camera className="h-4 w-4" />
                    Сканировать
                  </button>
                  <button
                    type="button"
                    disabled={validating || !credential.trim()}
                    onClick={() => validateCredential()}
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
                  >
                    {validating ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                    Проверить
                  </button>
                </div>
                {validationResult && (
                  <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-900 dark:bg-emerald-950/30">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 font-bold text-emerald-800 dark:text-emerald-200">
                        <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                        Код действителен
                      </div>
                      <button
                        type="button"
                        onClick={() => { setValidationResult(null); setCredential(''); }}
                        className="text-xs font-semibold text-emerald-700 underline hover:no-underline dark:text-emerald-300"
                      >
                        Сбросить
                      </button>
                    </div>
                    <div className="mt-3 grid gap-2 text-sm text-emerald-900 dark:text-emerald-100 sm:grid-cols-2">
                      <div>
                        <span className="text-xs text-emerald-700 dark:text-emerald-400">Клиент:</span>
                        <strong className="block">{validationResult.client?.name || 'Клиент приложения'}</strong>
                      </div>
                      <div>
                        <span className="text-xs text-emerald-700 dark:text-emerald-400">Телефон:</span>
                        <strong className="block">{validationResult.client?.phone || '—'}</strong>
                      </div>
                      <div>
                        <span className="text-xs text-emerald-700 dark:text-emerald-400">Тип привилегии:</span>
                        <strong className="block">{validationResult.benefit_label || 'Скидка постоянного клиента'}</strong>
                      </div>
                      <div>
                        <span className="text-xs text-emerald-700 dark:text-emerald-400">Размер скидки:</span>
                        <strong className="block text-emerald-700 dark:text-emerald-300 text-base">{validationResult.discount_percent}%</strong>
                      </div>
                    </div>
                    {validationResult.valid_until && (
                      <p className="mt-3 border-t border-emerald-200/60 pt-2 text-xs text-emerald-700 dark:border-emerald-800/60 dark:text-emerald-400">
                        Действует до: {new Date(validationResult.valid_until).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                      </p>
                    )}
                  </div>
                )}
              </section>
            )}
          </>
        )
      )}

      <ClientQrScanner open={scannerOpen} onClose={() => setScannerOpen(false)} onDetected={validateCredential} />
    </div>
  );
};

const ConfigurationError = ({ error, onRetry }) => (
  <div className="rounded-2xl border border-red-200 bg-white p-6 text-center shadow-sm dark:border-red-900 dark:bg-gray-900">
    <AlertCircle className="mx-auto h-8 w-8 text-red-500" />
    <p className="mt-3 text-sm text-red-700 dark:text-red-300">{error}</p>
    <button type="button" onClick={onRetry} className="mt-4 inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white"><RefreshCw className="h-4 w-4" />Повторить</button>
  </div>
);

export default ClientAppManagement;
